import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { teamApi, contactsApi, projectsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { User, Users, Plus, X } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = (slug) => (slug || "").split("_").map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
const ACCESS_LABEL = (slug) => ({ view: "View", contribute: "Contribute", manage_project: "Manage Project" })[slug] || ROLE_LABEL(slug);

/**
 * Project-side Contact / Member Roster (consolidated build §1).
 *
 * One canonical view inside a Project showing:
 *   - Linked Contacts (people the project cares about, not necessarily team)
 *   - Team Members (users with project memberships, with Role + Access Level)
 *
 * Admin can add/remove Contacts from the project here; both sides stay in
 * sync because this component writes through the SAME canonical endpoints
 * the Contact detail page uses (`/projects/{id}/contacts`,
 * `/contacts/{id}/project-links`). No parallel relationship model.
 */
export default function ProjectRosterPanel({ project, isAdmin, canManage }) {
  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState([]);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        contactsApi.listForProject(project.id),
        isAdmin ? teamApi.listProjectMembers(project.id) : teamApi.listProjectMembers(project.id),
      ]);
      setContacts(c.data || []);
      setMembers(m.data || []);
    } catch (e) { /* best-effort */ }
  }, [project.id, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const openAddContact = async () => {
    try {
      const r = await contactsApi.list();
      setAllContacts(r.data || []);
      setAddContactOpen(true);
    } catch { toast.error("Could not load contacts"); }
  };
  const linkContact = async () => {
    if (!picked) return toast.error("Pick a contact");
    try {
      await contactsApi.addProjectLink(picked, project.id);
      toast.success("Contact linked");
      setAddContactOpen(false); setPicked(""); setSearch("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
  };
  const unlinkContact = async (cid) => {
    if (!window.confirm("Remove this Contact from the Project? The Contact itself will stay.")) return;
    try {
      await contactsApi.removeProjectLink(cid, project.id);
      toast.success("Contact removed from project");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const linkedIds = new Set(contacts.map(c => c.id));
  const searchable = allContacts
    .filter(c => !linkedIds.has(c.id))
    .filter(c => !search.trim() || (c.full_name || "").toLowerCase().includes(search.toLowerCase()) || (c.email || "").toLowerCase().includes(search.toLowerCase()))
    .slice(0, 50);

  return (
    <div className="mt-3 p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-3" data-testid={`project-roster-${project.id}`}>
      {/* CONTACTS */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] uppercase tracking-wider text-white/50 flex items-center gap-1.5">
            <User className="w-3 h-3" /> Linked Contacts ({contacts.length})
          </div>
          {canManage && (
            <button onClick={openAddContact}
                    className="text-[11px] text-white/60 hover:text-white flex items-center gap-1"
                    data-testid={`project-roster-add-contact-${project.id}`}>
              <Plus className="w-3 h-3" /> Add Contact
            </button>
          )}
        </div>
        {contacts.length === 0 && <div className="text-[11px] text-white/40 italic">None linked.</div>}
        <div className="space-y-1">
          {contacts.map(c => (
            <div key={c.id}
                 className="flex items-center justify-between gap-2 text-xs py-1 px-2 rounded hover:bg-white/[0.03]"
                 data-testid={`project-roster-contact-${c.id}`}>
              <Link to={`/contacts/${c.id}`} className="text-white/85 hover:underline truncate">
                {c.full_name}<span className="text-white/40"> · {c.role || c.company || c.email || ""}</span>
              </Link>
              {canManage && (
                <button onClick={() => unlinkContact(c.id)}
                        className="text-red-400 hover:underline text-[11px]"
                        data-testid={`project-roster-contact-remove-${c.id}`}>
                  Remove from Project
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* MEMBERS */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] uppercase tracking-wider text-white/50 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Team Members ({members.length})
          </div>
          {isAdmin && (
            <Link to="/team"
                  className="text-[11px] text-white/60 hover:text-white"
                  data-testid={`project-roster-manage-members-${project.id}`}>
              Manage →
            </Link>
          )}
        </div>
        {members.length === 0 && <div className="text-[11px] text-white/40 italic">No team members yet.</div>}
        <div className="space-y-1">
          {members.map(m => (
            <div key={m.id}
                 className="flex items-center justify-between gap-2 text-xs py-1 px-2 rounded hover:bg-white/[0.03]"
                 data-testid={`project-roster-member-${m.id}`}>
              <div className="min-w-0">
                {isAdmin ? (
                  <Link to={`/member/${m.user_id}`} className="text-white/85 hover:underline">
                    {m.user_name || "—"}<span className="text-white/40"> · {m.user_email || ""}</span>
                  </Link>
                ) : (
                  <span className="text-white/85">
                    {m.user_name || "—"}<span className="text-white/40"> · {m.user_email || ""}</span>
                  </span>
                )}
                <div className="text-[10px] text-white/50 mt-0.5">
                  Role: <span className="text-white/70">{ROLE_LABEL(m.project_role) || "—"}</span>
                  {" · "}Access: <span className="text-white/70">{ACCESS_LABEL(m.access_level) || "Contribute"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ADD CONTACT DIALOG */}
      <Dialog open={addContactOpen} onOpenChange={(v) => !v && setAddContactOpen(false)}>
        <DialogContent className="max-w-md" data-testid="project-roster-add-contact-dialog">
          <DialogHeader><DialogTitle>Add existing Contact to Project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Search contacts…" value={search} onChange={e => setSearch(e.target.value)}
                   data-testid="project-roster-add-contact-search"
                   className="bg-white/5 border-white/10 text-sm" />
            <Select value={picked} onValueChange={setPicked}>
              <SelectTrigger data-testid="project-roster-add-contact-select">
                <SelectValue placeholder="Pick a contact to link" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {searchable.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.full_name} {c.role || c.company || c.email ? `· ${c.role || c.company || c.email}` : ""}
                  </SelectItem>
                ))}
                {searchable.length === 0 && <div className="p-2 text-sm text-white/40">No matching contacts.</div>}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddContactOpen(false)}>Cancel</Button>
            <Button onClick={linkContact} className="bg-white text-black hover:bg-white/90" data-testid="project-roster-add-contact-confirm">
              Add to Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
